// ConveyorTile.ts
//
// Define all the enums and types needed for conveyor tiles. Also
// define any methods needed to generate and manipulate this data.

import ConveyorTileActionResult, {
	ConveyorTileActionResultType,
} from 'app/src/engine/action-results/ConveyorTileActionResult';
import {
	MoveMovableItemActionResultApproachingTileFromDirection,
	MoveMovableItemActionResultDirection,
} from 'app/src/engine/action-results/MoveMovableItemActionResult';
import {
	MovableItemOrientation,
	MovableItemRotationAmount,
	rotateMovableItemOrientation,
	turnMovableItem,
} from 'app/src/engine/movable-items/MovableItem';
import Tile from 'app/src/engine/tiles/Tile';

export const tileName = 'ConveyorTile';

export enum ConveyorTileArrowError {
	InvalidArrow,
}

export enum ConveyorTileArrow {
	Left,
	Up,
	Right,
	Down,
	LeftFromTop,
	LeftFromBottom,
	UpFromLeft,
	UpFromRight,
	RightFromTop,
	RightFromBottom,
	DownFromLeft,
	DownFromRight,
	LeftAndFromTop,
	LeftAndFromBottom,
	UpAndFromLeft,
	UpAndFromRight,
	RightAndFromTop,
	RightAndFromBottom,
	DownAndFromLeft,
	DownAndFromRight,
	MergeLeft,
	MergeUp,
	MergeRight,
	MergeDown,
}

export function getConveyorTileArrow(name: string): ConveyorTileArrow {
	switch (name) {
		case 'Left':
			return ConveyorTileArrow.Left;
		case 'Up':
			return ConveyorTileArrow.Up;
		case 'Right':
			return ConveyorTileArrow.Right;
		case 'Down':
			return ConveyorTileArrow.Down;
		case 'LeftFromTop':
			return ConveyorTileArrow.LeftFromTop;
		case 'LeftFromBottom':
			return ConveyorTileArrow.LeftFromBottom;
		case 'UpFromLeft':
			return ConveyorTileArrow.UpFromLeft;
		case 'UpFromRight':
			return ConveyorTileArrow.UpFromRight;
		case 'RightFromTop':
			return ConveyorTileArrow.RightFromTop;
		case 'RightFromBottom':
			return ConveyorTileArrow.RightFromBottom;
		case 'DownFromLeft':
			return ConveyorTileArrow.DownFromLeft;
		case 'DownFromRight':
			return ConveyorTileArrow.DownFromRight;
		case 'LeftAndFromTop':
			return ConveyorTileArrow.LeftAndFromTop;
		case 'LeftAndFromBottom':
			return ConveyorTileArrow.LeftAndFromBottom;
		case 'UpAndFromLeft':
			return ConveyorTileArrow.UpAndFromLeft;
		case 'UpAndFromRight':
			return ConveyorTileArrow.UpAndFromRight;
		case 'RightAndFromTop':
			return ConveyorTileArrow.RightAndFromTop;
		case 'RightAndFromBottom':
			return ConveyorTileArrow.RightAndFromBottom;
		case 'DownAndFromLeft':
			return ConveyorTileArrow.DownAndFromLeft;
		case 'DownAndFromRight':
			return ConveyorTileArrow.DownAndFromRight;
		case 'MergeLeft':
			return ConveyorTileArrow.MergeLeft;
		case 'MergeUp':
			return ConveyorTileArrow.MergeUp;
		case 'MergeRight':
			return ConveyorTileArrow.MergeRight;
		case 'MergeDown':
			return ConveyorTileArrow.MergeDown;
		default:
			throw ConveyorTileArrowError.InvalidArrow;
	}
}

export default class ConveyorTile extends Tile {
	get tileName(): string {
		return tileName;
	}

	get arrow(): ConveyorTileArrow {
		return this._arrow;
	}

	private _arrow: ConveyorTileArrow;

	constructor(arrow: ConveyorTileArrow) {
		super();

		// Save params.
		this._arrow = arrow;
	}

	public getMoveDirection(): MoveMovableItemActionResultDirection {
		switch (this._arrow) {
			case ConveyorTileArrow.Up:
			case ConveyorTileArrow.UpFromLeft:
			case ConveyorTileArrow.UpFromRight:
			case ConveyorTileArrow.UpAndFromLeft:
			case ConveyorTileArrow.UpAndFromRight:
			case ConveyorTileArrow.MergeUp:
				return MoveMovableItemActionResultDirection.Up;

			case ConveyorTileArrow.Right:
			case ConveyorTileArrow.RightFromTop:
			case ConveyorTileArrow.RightFromBottom:
			case ConveyorTileArrow.RightAndFromTop:
			case ConveyorTileArrow.RightAndFromBottom:
			case ConveyorTileArrow.MergeRight:
				return MoveMovableItemActionResultDirection.Right;

			case ConveyorTileArrow.Down:
			case ConveyorTileArrow.DownFromRight:
			case ConveyorTileArrow.DownFromLeft:
			case ConveyorTileArrow.DownAndFromRight:
			case ConveyorTileArrow.DownAndFromLeft:
			case ConveyorTileArrow.MergeDown:
				return MoveMovableItemActionResultDirection.Down;

			case ConveyorTileArrow.Left:
			case ConveyorTileArrow.LeftFromBottom:
			case ConveyorTileArrow.LeftFromTop:
			case ConveyorTileArrow.LeftAndFromBottom:
			case ConveyorTileArrow.LeftAndFromTop:
			case ConveyorTileArrow.MergeLeft:
				return MoveMovableItemActionResultDirection.Left;
		}
	}

	public resolveConveyorTileActionResultConflict(
		result1: ConveyorTileActionResult,
		result2: ConveyorTileActionResult,
	): ConveyorTileActionResult {
		// Make sure these results _actually_ conflict.
		if (
			!(
				result1.location.row === result2.location.row &&
				result1.location.col === result2.location.col
			)
		) {
			throw new Error('ConveyorTileActionResults conflict resolution must occur on the same tile.');
		}

		// MoveConveyor trumps other types, so return it if either result has it.
		if (result1.type === ConveyorTileActionResultType.Move) {
			return result1;
		}

		// Other factors don't matter. Just pick the second.
		return result2;
	}

	public getMovableItemResultTurnsWhenBeingConveyedOntoThisTile(
		currentTurns: number,
		approachingFromDirection: MoveMovableItemActionResultApproachingTileFromDirection,
	): number {
		if (
			(this._arrow === ConveyorTileArrow.LeftFromTop &&
				approachingFromDirection ===
					MoveMovableItemActionResultApproachingTileFromDirection.FromTop) ||
			(this._arrow === ConveyorTileArrow.UpFromRight &&
				approachingFromDirection ===
					MoveMovableItemActionResultApproachingTileFromDirection.FromRight) ||
			(this._arrow === ConveyorTileArrow.RightFromBottom &&
				approachingFromDirection ===
					MoveMovableItemActionResultApproachingTileFromDirection.FromBottom) ||
			(this._arrow === ConveyorTileArrow.DownFromLeft &&
				approachingFromDirection ===
					MoveMovableItemActionResultApproachingTileFromDirection.FromLeft) ||
			(this._arrow === ConveyorTileArrow.LeftAndFromTop &&
				approachingFromDirection ===
					MoveMovableItemActionResultApproachingTileFromDirection.FromTop) ||
			(this._arrow === ConveyorTileArrow.UpAndFromRight &&
				approachingFromDirection ===
					MoveMovableItemActionResultApproachingTileFromDirection.FromRight) ||
			(this._arrow === ConveyorTileArrow.RightAndFromBottom &&
				approachingFromDirection ===
					MoveMovableItemActionResultApproachingTileFromDirection.FromBottom) ||
			(this._arrow === ConveyorTileArrow.DownAndFromLeft &&
				approachingFromDirection ===
					MoveMovableItemActionResultApproachingTileFromDirection.FromLeft) ||
			(this._arrow === ConveyorTileArrow.MergeUp &&
				approachingFromDirection ===
					MoveMovableItemActionResultApproachingTileFromDirection.FromRight) ||
			(this._arrow === ConveyorTileArrow.MergeRight &&
				approachingFromDirection ===
					MoveMovableItemActionResultApproachingTileFromDirection.FromBottom) ||
			(this._arrow === ConveyorTileArrow.MergeDown &&
				approachingFromDirection ===
					MoveMovableItemActionResultApproachingTileFromDirection.FromLeft) ||
			(this._arrow === ConveyorTileArrow.MergeLeft &&
				approachingFromDirection ===
					MoveMovableItemActionResultApproachingTileFromDirection.FromTop)
		) {
			return turnMovableItem(currentTurns, MovableItemRotationAmount.TurnRight);
		}

		if (
			(this._arrow === ConveyorTileArrow.LeftFromBottom &&
				approachingFromDirection ===
					MoveMovableItemActionResultApproachingTileFromDirection.FromBottom) ||
			(this._arrow === ConveyorTileArrow.UpFromLeft &&
				approachingFromDirection ===
					MoveMovableItemActionResultApproachingTileFromDirection.FromLeft) ||
			(this._arrow === ConveyorTileArrow.RightFromTop &&
				approachingFromDirection ===
					MoveMovableItemActionResultApproachingTileFromDirection.FromTop) ||
			(this._arrow === ConveyorTileArrow.DownFromRight &&
				approachingFromDirection ===
					MoveMovableItemActionResultApproachingTileFromDirection.FromRight) ||
			(this._arrow === ConveyorTileArrow.LeftAndFromBottom &&
				approachingFromDirection ===
					MoveMovableItemActionResultApproachingTileFromDirection.FromBottom) ||
			(this._arrow === ConveyorTileArrow.UpAndFromLeft &&
				approachingFromDirection ===
					MoveMovableItemActionResultApproachingTileFromDirection.FromLeft) ||
			(this._arrow === ConveyorTileArrow.RightAndFromTop &&
				approachingFromDirection ===
					MoveMovableItemActionResultApproachingTileFromDirection.FromTop) ||
			(this._arrow === ConveyorTileArrow.DownAndFromRight &&
				approachingFromDirection ===
					MoveMovableItemActionResultApproachingTileFromDirection.FromRight) ||
			(this._arrow === ConveyorTileArrow.MergeUp &&
				approachingFromDirection ===
					MoveMovableItemActionResultApproachingTileFromDirection.FromLeft) ||
			(this._arrow === ConveyorTileArrow.MergeRight &&
				approachingFromDirection ===
					MoveMovableItemActionResultApproachingTileFromDirection.FromTop) ||
			(this._arrow === ConveyorTileArrow.MergeDown &&
				approachingFromDirection ===
					MoveMovableItemActionResultApproachingTileFromDirection.FromRight) ||
			(this._arrow === ConveyorTileArrow.MergeLeft &&
				approachingFromDirection ===
					MoveMovableItemActionResultApproachingTileFromDirection.FromBottom)
		) {
			return turnMovableItem(currentTurns, MovableItemRotationAmount.TurnLeft);
		}

		return currentTurns;
	}

	public getMovableItemResultDirectionWhenBeingConveyedOntoThisTile(
		currentDirection: MovableItemOrientation,
		approachingFromDirection: MoveMovableItemActionResultApproachingTileFromDirection,
	): MovableItemOrientation {
		if (
			(this._arrow === ConveyorTileArrow.LeftFromTop &&
				approachingFromDirection ===
					MoveMovableItemActionResultApproachingTileFromDirection.FromTop) ||
			(this._arrow === ConveyorTileArrow.UpFromRight &&
				approachingFromDirection ===
					MoveMovableItemActionResultApproachingTileFromDirection.FromRight) ||
			(this._arrow === ConveyorTileArrow.RightFromBottom &&
				approachingFromDirection ===
					MoveMovableItemActionResultApproachingTileFromDirection.FromBottom) ||
			(this._arrow === ConveyorTileArrow.DownFromLeft &&
				approachingFromDirection ===
					MoveMovableItemActionResultApproachingTileFromDirection.FromLeft) ||
			(this._arrow === ConveyorTileArrow.LeftAndFromTop &&
				approachingFromDirection ===
					MoveMovableItemActionResultApproachingTileFromDirection.FromTop) ||
			(this._arrow === ConveyorTileArrow.UpAndFromRight &&
				approachingFromDirection ===
					MoveMovableItemActionResultApproachingTileFromDirection.FromRight) ||
			(this._arrow === ConveyorTileArrow.RightAndFromBottom &&
				approachingFromDirection ===
					MoveMovableItemActionResultApproachingTileFromDirection.FromBottom) ||
			(this._arrow === ConveyorTileArrow.DownAndFromLeft &&
				approachingFromDirection ===
					MoveMovableItemActionResultApproachingTileFromDirection.FromLeft) ||
			(this._arrow === ConveyorTileArrow.MergeUp &&
				approachingFromDirection ===
					MoveMovableItemActionResultApproachingTileFromDirection.FromRight) ||
			(this._arrow === ConveyorTileArrow.MergeRight &&
				approachingFromDirection ===
					MoveMovableItemActionResultApproachingTileFromDirection.FromBottom) ||
			(this._arrow === ConveyorTileArrow.MergeDown &&
				approachingFromDirection ===
					MoveMovableItemActionResultApproachingTileFromDirection.FromLeft) ||
			(this._arrow === ConveyorTileArrow.MergeLeft &&
				approachingFromDirection ===
					MoveMovableItemActionResultApproachingTileFromDirection.FromTop)
		) {
			return rotateMovableItemOrientation(currentDirection, MovableItemRotationAmount.TurnRight);
		}

		if (
			(this._arrow === ConveyorTileArrow.LeftFromBottom &&
				approachingFromDirection ===
					MoveMovableItemActionResultApproachingTileFromDirection.FromBottom) ||
			(this._arrow === ConveyorTileArrow.UpFromLeft &&
				approachingFromDirection ===
					MoveMovableItemActionResultApproachingTileFromDirection.FromLeft) ||
			(this._arrow === ConveyorTileArrow.RightFromTop &&
				approachingFromDirection ===
					MoveMovableItemActionResultApproachingTileFromDirection.FromTop) ||
			(this._arrow === ConveyorTileArrow.DownFromRight &&
				approachingFromDirection ===
					MoveMovableItemActionResultApproachingTileFromDirection.FromRight) ||
			(this._arrow === ConveyorTileArrow.LeftAndFromBottom &&
				approachingFromDirection ===
					MoveMovableItemActionResultApproachingTileFromDirection.FromBottom) ||
			(this._arrow === ConveyorTileArrow.UpAndFromLeft &&
				approachingFromDirection ===
					MoveMovableItemActionResultApproachingTileFromDirection.FromLeft) ||
			(this._arrow === ConveyorTileArrow.RightAndFromTop &&
				approachingFromDirection ===
					MoveMovableItemActionResultApproachingTileFromDirection.FromTop) ||
			(this._arrow === ConveyorTileArrow.DownAndFromRight &&
				approachingFromDirection ===
					MoveMovableItemActionResultApproachingTileFromDirection.FromRight) ||
			(this._arrow === ConveyorTileArrow.MergeUp &&
				approachingFromDirection ===
					MoveMovableItemActionResultApproachingTileFromDirection.FromLeft) ||
			(this._arrow === ConveyorTileArrow.MergeRight &&
				approachingFromDirection ===
					MoveMovableItemActionResultApproachingTileFromDirection.FromTop) ||
			(this._arrow === ConveyorTileArrow.MergeDown &&
				approachingFromDirection ===
					MoveMovableItemActionResultApproachingTileFromDirection.FromRight) ||
			(this._arrow === ConveyorTileArrow.MergeLeft &&
				approachingFromDirection ===
					MoveMovableItemActionResultApproachingTileFromDirection.FromBottom)
		) {
			return rotateMovableItemOrientation(currentDirection, MovableItemRotationAmount.TurnLeft);
		}

		return currentDirection;
	}

	public isEqualTo(tile: ConveyorTile): boolean {
		return tile.arrow === this.arrow;
	}

	public clone(): ConveyorTile {
		const clone = new ConveyorTile(this.arrow);
		clone.walls = new Set(this.walls);
		clone.allowsMultipleMovableItems = this.allowsMultipleMovableItems;
		return clone;
	}
}

export function rotateConveyorTile(tile: ConveyorTile, clockwise: boolean): ConveyorTile {
	let arrow = tile.arrow;

	if (clockwise) {
		switch (arrow) {
			case ConveyorTileArrow.Up:
				arrow = ConveyorTileArrow.Right;
				break;
			case ConveyorTileArrow.Right:
				arrow = ConveyorTileArrow.Down;
				break;
			case ConveyorTileArrow.Down:
				arrow = ConveyorTileArrow.Left;
				break;
			case ConveyorTileArrow.Left:
				arrow = ConveyorTileArrow.Up;
				break;

			case ConveyorTileArrow.LeftFromBottom:
				arrow = ConveyorTileArrow.UpFromLeft;
				break;
			case ConveyorTileArrow.UpFromLeft:
				arrow = ConveyorTileArrow.RightFromTop;
				break;
			case ConveyorTileArrow.RightFromTop:
				arrow = ConveyorTileArrow.DownFromRight;
				break;
			case ConveyorTileArrow.DownFromRight:
				arrow = ConveyorTileArrow.LeftFromBottom;
				break;

			case ConveyorTileArrow.LeftFromTop:
				arrow = ConveyorTileArrow.UpFromRight;
				break;
			case ConveyorTileArrow.UpFromRight:
				arrow = ConveyorTileArrow.RightFromBottom;
				break;
			case ConveyorTileArrow.RightFromBottom:
				arrow = ConveyorTileArrow.DownFromLeft;
				break;
			case ConveyorTileArrow.DownFromLeft:
				arrow = ConveyorTileArrow.LeftFromTop;
				break;

			case ConveyorTileArrow.LeftAndFromBottom:
				arrow = ConveyorTileArrow.UpAndFromLeft;
				break;
			case ConveyorTileArrow.UpAndFromLeft:
				arrow = ConveyorTileArrow.RightAndFromTop;
				break;
			case ConveyorTileArrow.RightAndFromTop:
				arrow = ConveyorTileArrow.DownAndFromRight;
				break;
			case ConveyorTileArrow.DownAndFromRight:
				arrow = ConveyorTileArrow.LeftAndFromBottom;
				break;

			case ConveyorTileArrow.LeftAndFromTop:
				arrow = ConveyorTileArrow.UpAndFromRight;
				break;
			case ConveyorTileArrow.UpAndFromRight:
				arrow = ConveyorTileArrow.RightAndFromBottom;
				break;
			case ConveyorTileArrow.RightAndFromBottom:
				arrow = ConveyorTileArrow.DownAndFromLeft;
				break;
			case ConveyorTileArrow.DownAndFromLeft:
				arrow = ConveyorTileArrow.LeftAndFromTop;
				break;

			case ConveyorTileArrow.MergeUp:
				arrow = ConveyorTileArrow.MergeRight;
				break;
			case ConveyorTileArrow.MergeRight:
				arrow = ConveyorTileArrow.MergeDown;
				break;
			case ConveyorTileArrow.MergeDown:
				arrow = ConveyorTileArrow.MergeLeft;
				break;
			case ConveyorTileArrow.MergeLeft:
				arrow = ConveyorTileArrow.MergeUp;
				break;
		}
	} else {
		switch (arrow) {
			case ConveyorTileArrow.Up:
				arrow = ConveyorTileArrow.Left;
				break;
			case ConveyorTileArrow.Right:
				arrow = ConveyorTileArrow.Up;
				break;
			case ConveyorTileArrow.Down:
				arrow = ConveyorTileArrow.Right;
				break;
			case ConveyorTileArrow.Left:
				arrow = ConveyorTileArrow.Down;
				break;

			case ConveyorTileArrow.LeftFromBottom:
				arrow = ConveyorTileArrow.DownFromRight;
				break;
			case ConveyorTileArrow.UpFromLeft:
				arrow = ConveyorTileArrow.LeftFromBottom;
				break;
			case ConveyorTileArrow.RightFromTop:
				arrow = ConveyorTileArrow.UpFromLeft;
				break;
			case ConveyorTileArrow.DownFromRight:
				arrow = ConveyorTileArrow.RightFromTop;
				break;

			case ConveyorTileArrow.LeftFromTop:
				arrow = ConveyorTileArrow.DownFromLeft;
				break;
			case ConveyorTileArrow.UpFromRight:
				arrow = ConveyorTileArrow.LeftFromTop;
				break;
			case ConveyorTileArrow.RightFromBottom:
				arrow = ConveyorTileArrow.UpFromRight;
				break;
			case ConveyorTileArrow.DownFromLeft:
				arrow = ConveyorTileArrow.RightFromBottom;
				break;

			case ConveyorTileArrow.LeftAndFromBottom:
				arrow = ConveyorTileArrow.DownAndFromRight;
				break;
			case ConveyorTileArrow.UpAndFromLeft:
				arrow = ConveyorTileArrow.LeftAndFromBottom;
				break;
			case ConveyorTileArrow.RightAndFromTop:
				arrow = ConveyorTileArrow.UpAndFromLeft;
				break;
			case ConveyorTileArrow.DownAndFromRight:
				arrow = ConveyorTileArrow.RightAndFromTop;
				break;

			case ConveyorTileArrow.LeftAndFromTop:
				arrow = ConveyorTileArrow.DownAndFromLeft;
				break;
			case ConveyorTileArrow.UpAndFromRight:
				arrow = ConveyorTileArrow.LeftAndFromTop;
				break;
			case ConveyorTileArrow.RightAndFromBottom:
				arrow = ConveyorTileArrow.UpAndFromRight;
				break;
			case ConveyorTileArrow.DownAndFromLeft:
				arrow = ConveyorTileArrow.RightAndFromBottom;
				break;

			case ConveyorTileArrow.MergeUp:
				arrow = ConveyorTileArrow.MergeLeft;
				break;
			case ConveyorTileArrow.MergeRight:
				arrow = ConveyorTileArrow.MergeUp;
				break;
			case ConveyorTileArrow.MergeDown:
				arrow = ConveyorTileArrow.MergeRight;
				break;
			case ConveyorTileArrow.MergeLeft:
				arrow = ConveyorTileArrow.MergeDown;
				break;
		}
	}

	return new ConveyorTile(arrow);
}
